// ClinicsTableDesktop.jsx
import Table, {
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "@components/ui/Table";
import Button from "@components/ui/Button";
import Truncate from "@components/ui/Truncate";
import { isPmsConfigured } from "./SchedulingEditor";

function StatusPill({ enabled }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
      }`}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function RatingDisplay({ stats }) {
  if (!stats || stats.total_rated === 0) {
    return <span className="text-gray-400 text-sm">No ratings</span>;
  }

  const score = stats.average_score;
  const getScoreColor = (score) => {
    if (score >= 4) return "text-green-600";
    if (score >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 4) return "bg-green-100";
    if (score >= 3) return "bg-yellow-100";
    return "bg-red-100";
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-semibold ${getScoreBg(score)} ${getScoreColor(score)}`}
        >
          {score.toFixed(1)} / 5
        </span>
      </div>
      <div className="text-xs text-gray-500">
        {stats.total_rated} call{stats.total_rated !== 1 ? "s" : ""} rated
      </div>
      {stats.needs_review > 0 && (
        <div className="text-xs text-orange-600 font-medium">
          {stats.needs_review} need{stats.needs_review !== 1 ? "" : "s"} review
        </div>
      )}
    </div>
  );
}

export default function ClinicsTableDesktop({ clinics, onEdit, onView, onImpersonate, onRunSetup, setupLoading, onReport }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell className="w-[18%] min-w-[180px]">
            Clinic
          </TableHeaderCell>
          <TableHeaderCell className="w-[10%] min-w-[100px]">
            Phone
          </TableHeaderCell>
          <TableHeaderCell className="w-[10%] min-w-[100px]">
            Timezone
          </TableHeaderCell>
          <TableHeaderCell className="w-[12%] min-w-[120px]">
            Agent
          </TableHeaderCell>
          <TableHeaderCell className="w-[12%] min-w-[120px]">
            Call Rating
          </TableHeaderCell>
          <TableHeaderCell className="w-[14%] min-w-[140px]">
            UniFi
          </TableHeaderCell>
          <TableHeaderCell className="w-[12%] min-w-[140px]">
            Quick View
          </TableHeaderCell>
          <TableHeaderCell className="w-[8%] min-w-[100px]">
            Actions
          </TableHeaderCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {clinics.map((c) => {
          const inactive = c.is_active === false;
          return (
          <TableRow
            key={c.id}
            className={`hover:bg-gray-50 ${inactive ? "opacity-60" : ""}`}
          >
            <TableCell className="align-top">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="font-medium text-sm">
                  {c.clinic_name || "—"}
                </div>
                {inactive && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    Inactive
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 break-all truncate">
                {c.id}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Address:</span>{" "}
                <Truncate text={c.address} max={42} />
              </div>
            </TableCell>

            <TableCell className="align-top">
              <Truncate text={c.phone} max={18} className="text-sm" />
            </TableCell>

            <TableCell className="align-top">
              <Truncate
                text={c.preferred_timezone}
                max={18}
                className="text-sm"
              />
            </TableCell>

            <TableCell className="align-top">
              <Truncate
                text={c.clinic_agent_name}
                max={24}
                className="text-sm"
              />
            </TableCell>

            <TableCell className="align-top">
              <RatingDisplay stats={c.rating_stats} />
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <StatusPill enabled={!!c.unifi_scraping_enabled} />
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Console:</span>{" "}
                  <span className="break-all">
                    {c.unifi_console_id ? (
                      <Truncate text={c.unifi_console_id} max={28} />
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
              </div>
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <Button
                  color="light"
                  size="xs"
                  onClick={() =>
                    onView(`${c.clinic_name || "Clinic"} Info`, c.clinic_info)
                  }
                >
                  Info
                </Button>

                <Button
                  color="light"
                  size="xs"
                  onClick={() =>
                    onView(
                      `${c.clinic_name || "Clinic"} Welcome Message`,
                      c.welcome_message
                    )
                  }
                >
                  Welcome
                </Button>

                <Button
                  color="light"
                  size="xs"
                  onClick={() =>
                    onView(
                      `${c.clinic_name || "Clinic"} Agent Questions`,
                      JSON.stringify(c.agent_questions || {}, null, 2),
                      true
                    )
                  }
                >
                  Questions
                </Button>
              </div>
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <Button size="sm" onClick={() => onEdit(c)} className="w-full">
                  Edit
                </Button>
                <Button
                  size="sm"
                  color="purple"
                  onClick={() => onReport(c)}
                  className="w-full"
                >
                  Report
                </Button>
                {c.owner_user_id && (
                  <Button
                    size="sm"
                    color="amber"
                    onClick={() => onImpersonate(c)}
                    className="w-full"
                  >
                    Login As
                  </Button>
                )}
                {isPmsConfigured(c.active_pms_provider, c.pms_config) && (
                  <Button
                    size="sm"
                    color="green"
                    disabled={setupLoading}
                    onClick={() => onRunSetup(c.id)}
                    className="w-full"
                  >
                    {setupLoading ? "Setting up…" : "Run Setup"}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
          );
        })}

        {clinics.length === 0 && (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8">
              <div className="text-gray-500">No clinics match your search.</div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
